import { type Editor, EditorContent } from '@tiptap/react';
import { type MouseEvent, memo, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { ArrowUpIcon, SquareIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { PrivateWorkspaceAgentActionsMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/PrivateWorkspaceAgentActionsMenu';
import { WorkspaceAgentMentionPopup } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionPopup';
import { PrivateWorkspaceAgentComposerScopeBar } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentComposerScopeBar';
import { PrivateWorkspaceAgentPanelComposerCreditsDepletedMessage } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentPanelComposerCreditsDepletedMessage';
import { WorkspaceAgentSkillTriggerMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/skills/WorkspaceAgentSkillTriggerMenu';
import {
  workspaceAgentComposerActionRow,
  workspaceAgentComposerActionRowGroup,
  workspaceAgentComposerEditor,
  workspaceAgentComposerEditorContent,
  workspaceAgentComposerEditorSlot,
  workspaceAgentComposerFocusCapture,
  workspaceAgentComposerForeground,
  workspaceAgentComposerInputRow,
  workspaceAgentComposerScopeSlotPadding,
  workspaceAgentComposerSendButton,
  workspaceAgentComposerShell,
  workspaceAgentComposerStopButton,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { usePrivateWorkspaceAgentComposerEditor } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerEditor';
import { usePrivateWorkspaceAgentComposerScopeChip } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerScopeChip';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';

const SEND_BUTTON_SUFFIX = <ArrowUpIcon aria-hidden size="xs" />;

export type PrivateWorkspaceAgentPanelComposerProps = {
  isSuppressingEditorAgentDrag: boolean;
  onEditorChange: (editor: Editor | null) => void;
};

export const PrivateWorkspaceAgentPanelComposer = memo(
  ({ isSuppressingEditorAgentDrag, onEditorChange }: PrivateWorkspaceAgentPanelComposerProps) => {
    const { isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
    const { scopeChip } = usePrivateWorkspaceAgentComposerScopeChip();
    const {
      canSubmit,
      composerPlaceholder,
      composerRunStatusMessage,
      composerStopTooltip,
      editor,
      handleAddReference,
      handleInsertSkillFromActionsMenu,
      handleSendKeyDown,
      handleStop,
      handleStopKeyDown,
      handleStopMouseDown,
      handleSubmit,
      isComposerLocked,
      isComposerRunLocked,
      isStopEnabled,
      isStopLoading,
      mentionPopupProps,
      mentionPopupRef,
      refocusEditor,
      skillPopupProps,
      skillPopupRef,
    } = usePrivateWorkspaceAgentComposerEditor();
    /** What the `@` picker anchors to: it sits flush on this box's top edge and takes its width. */
    const composerShellRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      onEditorChange(editor);
    }, [editor, onEditorChange]);

    // Closing Actions restores focus to the trigger inside this shell. The editor blur on lock
    // does not cover that, so the locked composer would keep a focus-within ring.
    useIsomorphicLayoutEffect(() => {
      if (!isComposerLocked) {
        return;
      }

      const shell = composerShellRef.current;
      const activeElement = document.activeElement;

      if (!(activeElement instanceof HTMLElement) || !shell?.contains(activeElement)) {
        return;
      }

      activeElement.blur();
    }, [isComposerLocked]);

    const handleFocusCaptureMouseDown = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        refocusEditor();
      },
      [refocusEditor],
    );

    const stopButton = (
      <IconButton
        key="stop"
        appearance="filled"
        aria-disabled={!isStopEnabled}
        aria-busy={isStopLoading}
        className={workspaceAgentComposerStopButton({
          isInteractionDisabled: !isStopEnabled && !isStopLoading,
        })}
        color="gray"
        data-testid="WORKSPACE_AGENT_COMPOSER_STOP"
        isLoading={isStopLoading}
        label="Stop"
        onClick={handleStop}
        onKeyDown={handleStopKeyDown}
        onMouseDown={handleStopMouseDown}
        size="small"
      >
        <SquareIcon aria-hidden className="fill-current" size="xs" />
      </IconButton>
    );

    let actionButton: ReactNode;

    if (isComposerRunLocked) {
      actionButton = composerStopTooltip ? (
        <Tooltip label={composerStopTooltip} side="bottom" sideOffset={4}>
          {stopButton}
        </Tooltip>
      ) : (
        stopButton
      );
    } else {
      actionButton = (
        <Button
          key="send"
          appearance="filled"
          // Not `disabled`, which would take the only send control out of the tab order. `handleSubmit`
          // already no-ops when there is nothing to send; only the look has to be restated, since the
          // primitive's greyed-out state keys off the `disabled:` variant.
          aria-disabled={!canSubmit}
          className={workspaceAgentComposerSendButton()}
          data-testid="WORKSPACE_AGENT_COMPOSER_SEND"
          onClick={handleSubmit}
          onKeyDown={handleSendKeyDown}
          size="small"
          suffix={SEND_BUTTON_SUFFIX}
        >
          Send
        </Button>
      );
    }

    const showDepletedBannerInComposer = isCreditsDepleted && isComposerRunLocked;

    return (
      <div
        aria-disabled={isComposerLocked || undefined}
        className={workspaceAgentComposerShell()}
        data-testid="WORKSPACE_AGENT_COMPOSER"
        ref={composerShellRef}
      >
        {composerRunStatusMessage ? <output className="sr-only">{composerRunStatusMessage}</output> : null}
        <div aria-hidden className={workspaceAgentComposerFocusCapture()} onMouseDown={handleFocusCaptureMouseDown} />
        <div className={workspaceAgentComposerForeground()}>
          <div className={workspaceAgentComposerInputRow()}>
            {isCreditsDepleted ? null : <PrivateWorkspaceAgentComposerScopeBar onAfterRemove={refocusEditor} />}
            <div
              className={workspaceAgentComposerEditorSlot({ isCreditsDepleted: showDepletedBannerInComposer })}
              data-testid="WORKSPACE_AGENT_COMPOSER_EDITOR_SLOT"
            >
              {showDepletedBannerInComposer ? (
                <div className="w-full" data-testid="WORKSPACE_AGENT_COMPOSER_CREDITS_DEPLETED_BANNER">
                  <PrivateWorkspaceAgentPanelComposerCreditsDepletedMessage />
                </div>
              ) : (
                <div className={workspaceAgentComposerScopeSlotPadding({ hasScope: scopeChip !== null })}>
                  <div className={workspaceAgentComposerEditor()} data-testid="WORKSPACE_AGENT_COMPOSER_EDITOR">
                    <EditorContent
                      className={workspaceAgentComposerEditorContent({
                        isAgentSelectionDropTarget: isSuppressingEditorAgentDrag,
                      })}
                      data-placeholder={composerPlaceholder}
                      data-testid="WORKSPACE_AGENT_COMPOSER_EDITOR_CONTENT"
                      editor={editor}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={workspaceAgentComposerActionRow()}>
            <div className={workspaceAgentComposerActionRowGroup()}>
              {isCreditsDepleted ? null : (
                <PrivateWorkspaceAgentActionsMenu
                  anchorRef={composerShellRef}
                  isDisabled={isComposerRunLocked || isComposerLocked}
                  onAddReference={handleAddReference}
                  onInsertSkill={handleInsertSkillFromActionsMenu}
                />
              )}
            </div>
            <div className={workspaceAgentComposerActionRowGroup()}>{actionButton}</div>
          </div>
        </div>
        {!isCreditsDepleted && skillPopupProps ? (
          <WorkspaceAgentSkillTriggerMenu
            {...skillPopupProps}
            anchorRef={composerShellRef}
            key={skillPopupProps.menuListKey}
            ref={skillPopupRef}
          />
        ) : null}
        {!isCreditsDepleted && mentionPopupProps ? (
          <WorkspaceAgentMentionPopup {...mentionPopupProps} anchorRef={composerShellRef} ref={mentionPopupRef} />
        ) : null}
      </div>
    );
  },
);

PrivateWorkspaceAgentPanelComposer.displayName = 'PrivateWorkspaceAgentPanelComposer';
